import React from 'react';
import { Col, Row, Space, Typography, Card, Divider, Tag, Grid } from "antd";
import { t } from "i18next";
import moment from "moment";
import { MdCancel, MdPerson, MdDateRange, MdAccessTime, MdLocalHospital, MdOutlineDescription } from "react-icons/md";
import { BsFillCheckCircleFill, BsGenderMale, BsGenderFemale, BsTelephone, BsEnvelope } from "react-icons/bs";
import Helper from "../../../core/Helper";

const { Text, Title } = Typography;

export default function AppointmentPreview({ patient, appointment }) {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Mobil-aware boyutlar
    const cardPadding = isMobile ? 12 : 12;
    const cardRadius = isMobile ? 8 : 12;
    const titleFontSize = isMobile ? 17 : 24;
    const subTitleFontSize = isMobile ? 16 : 24;
    const stateTagFontSize = isMobile ? 14 : 13;
    const dateMainFontSize = isMobile ? 17 : 16;

    // Yardımcı: İkonlu Bilgi Satırı
    const InfoRow = ({ icon, title, value, highlight = false, iconColor = '#1890ff' }) => (
        <div style={{ marginBottom: isMobile ? 10 : 12, display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ marginTop: 4, marginRight: 8, color: iconColor, flexShrink: 0 }}>
                {icon}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
                <Text
                    strong={highlight}
                    style={{
                        fontSize: highlight ? 15 : 14,
                        wordBreak: 'break-word'
                    }}
                >
                    {value || '--'}
                </Text>
            </div>
        </div>
    );

    // Yardımcı: Durum Rengi ve Etiketi
    const renderStateTag = (state) => {
        if (state) {
            return (
                <Tag
                    color={state.color}
                    style={{
                        margin: 0,
                        padding: isMobile ? '4px 12px' : '4px 10px',
                        fontSize: stateTagFontSize
                    }}
                >
                    {state.name}
                </Tag>
            );
        }
        return <Tag>--</Tag>;
    };

    // Copyable config — mobilde daha büyük icon
    const copyableConfig = (text) => ({
        text,
        tooltips: false
    });

    // Hasta kartı rengi cinsiyete göre: erkek (M) mavi, kadın (F) pembe, diğer/boş (O/null) gri.
    // PatientApp ile aynı mantık; burada üst border + kart arka planı + avatar boyanır.
    const genderTheme = (() => {
        const g = patient?.gender;
        if (g === 'M') return { border: '#1890ff', bg: '#f0f5ff', avatar: '#adc6ff' }; // erkek — mavi (geekblue tonu; antd seçili-satır rengiyle karışmasın)
        if (g === 'F') return { border: '#eb2f96', bg: '#fff0f6', avatar: '#ffd6e7' }; // kadın — pembe
        return { border: '#8c8c8c', bg: '#fafafa', avatar: '#f0f0f0' };                // diğer/belirsiz — gri
    })();

    return (
        <div style={{ padding: isMobile ? 4 : 8 }}>
            <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]}>

                {/* --- SOL KART: HASTA BİLGİLERİ — mavi (PatientApp Hasta kartı ile aynı) --- */}
                <Col xs={24} md={10}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: cardRadius, borderTop: `4px solid ${genderTheme.border}` }}
                        bodyStyle={{ padding: cardPadding }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: isMobile ? 14 : 20,
                            gap: isMobile ? 10 : 16,
                            backgroundColor: genderTheme.bg,
                            border: `1px solid ${genderTheme.border}`,
                            borderRadius: 8,
                            padding: isMobile ? 8 : 10
                        }}>
                            <div style={{
                                width: isMobile ? 40 : 48,
                                height: isMobile ? 40 : 48,
                                borderRadius: '50%',
                                backgroundColor: genderTheme.avatar,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: genderTheme.border,
                                fontSize: isMobile ? 20 : 24,
                                flexShrink: 0
                            }}>
                                <MdPerson />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <Title
                                    level={4}
                                    style={{
                                        margin: 0,
                                        fontSize: titleFontSize,
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3
                                    }}
                                    copyable={copyableConfig(`${patient.name || ''} ${patient.lastname || ''}`.trim())}
                                    ellipsis={isMobile ? { rows: 2, tooltip: `${patient.name || ''} ${patient.lastname || ''}` } : false}
                                >
                                    {patient.name} {patient.lastname}
                                </Title>
                                <Text
                                    type="secondary"
                                    copyable={copyableConfig(patient.customId || '')}
                                    style={{ fontSize: 12, wordBreak: 'break-word' }}
                                >
                                    ID: {patient.customId} {(patient.otherPatientId ? (` - ${patient.otherPatientId}`) : '')}
                                </Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '8px 0 16px 0' }} />

                        <InfoRow
                            iconColor={genderTheme.border}
                            icon={patient.gender === 'F' ? <BsGenderFemale /> : <BsGenderMale />}
                            title={t('Gender') + ' / ' + t('Birthdate')}
                            value={`${t(patient.gender)} • ${patient.birthDay ? `${patient.birthDay?.toString().padStart(2, '0')}/${patient.birthMonth?.toString().padStart(2, '0')}/${patient.birthYear}` : '--'}`}
                        />

                        <InfoRow
                            iconColor={genderTheme.border}
                            icon={<MdPerson />}
                            title={t('Mother Name') + ' / ' + t('Father Name')}
                            value={`${patient.motherName || '--'} • ${patient.fatherName || '--'}`}
                        />

                        <InfoRow
                            iconColor={genderTheme.border}
                            icon={<BsTelephone />}
                            title={t('Contact')}
                            value={
                                <Space direction="vertical" size={2}>
                                    {patient.phone1 && (() => {
                                        const formatted = Helper.Phone.intlPhoneFormat(patient.phoneCode1, patient.phone1);
                                        const cleaned = Helper.Phone.clearPhoneFormat(patient.phone1);
                                        const telHref = `tel:+${patient.phoneCode1 || ''}${cleaned}`;
                                        return (
                                            <Text copyable={copyableConfig(formatted)}>
                                                <a href={telHref} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                    {formatted}
                                                </a>
                                            </Text>
                                        );
                                    })()}
                                    {patient.phone2 && (() => {
                                        const formatted = Helper.Phone.intlPhoneFormat(patient.phoneCode2, patient.phone2);
                                        const cleaned = Helper.Phone.clearPhoneFormat(patient.phone2);
                                        const telHref = `tel:+${patient.phoneCode2 || ''}${cleaned}`;
                                        return (
                                            <Text type="secondary" copyable={copyableConfig(formatted)}>
                                                <a href={telHref} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                    {formatted}
                                                </a>
                                            </Text>
                                        );
                                    })()}
                                </Space>
                            }
                        />

                        {patient.email && (
                            <InfoRow
                                iconColor={genderTheme.border}
                                icon={<BsEnvelope />}
                                title={t('Email')}
                                value={
                                    <Text copyable={copyableConfig(patient.email)} style={{ wordBreak: 'break-all' }}>
                                        <a href={`mailto:${patient.email}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                            {patient.email}
                                        </a>
                                    </Text>
                                }
                            />
                        )}

                        {patient.note && (
                            <InfoRow
                                iconColor={genderTheme.border}
                                icon={<MdOutlineDescription />}
                                title={t('Patient Note')}
                                value={
                                    <div style={{
                                        maxHeight: isMobile ? 120 : 200,
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {patient.note}
                                    </div>
                                }
                            />
                        )}
                    </Card>
                </Col>

                {/* --- SAĞ KART: RANDEVU DETAYLARI — cyan (PatientApp Randevu kartı ile aynı) --- */}
                <Col xs={24} md={14}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: cardRadius, borderTop: '4px solid #13c2c2' }}
                        bodyStyle={{ padding: cardPadding }}
                    >
                        {/* Başlık Alanı — mobilde dikey stack, desktop'ta yan yana */}
                        <div style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            marginBottom: isMobile ? 14 : 20,
                            gap: 8
                        }}>
                            <Text
                                strong
                                style={{
                                    fontSize: titleFontSize,
                                    color: '#13c2c2',
                                    textTransform: isMobile ? 'none' : 'uppercase',
                                    letterSpacing: isMobile ? 'normal' : '0.5px',
                                    flex: 1,
                                    minWidth: 0,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.3
                                }}
                            >
                                {appointment.unit?.name}
                            </Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Title
                                    level={4}
                                    style={{
                                        margin: 0,
                                        fontSize: subTitleFontSize,
                                        color: '#262626',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3
                                    }}
                                >
                                    {appointment.institution?.name}
                                </Title>
                                {appointment.appointmentType?.name && (
                                    <Tag
                                        icon={<MdOutlineDescription style={{ marginRight: 4 }} />}
                                        style={{
                                            margin: 0,
                                            padding: '2px 8px',
                                            fontSize: 12,
                                            borderRadius: 4,
                                            background: 'rgba(0,0,0,0.02)',
                                            border: '1px solid #d9d9d9',
                                            display: 'inline-flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {appointment.appointmentType?.name}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        {/* Tarih ve Saat Kutusu (Vurgulu) */}
                        <div style={{
                            backgroundColor: '#f9f9f9',
                            padding: isMobile ? 12 : 16,
                            borderRadius: 8,
                            border: '1px solid #eee',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: isMobile ? 10 : 12,
                            marginBottom: isMobile ? 16 : 24
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, flex: 1 }}>
                                <MdDateRange size={24} color="#13c2c2" style={{ flexShrink: 0, marginTop: 2 }} />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <Text
                                        strong
                                        style={{
                                            display: 'block',
                                            fontSize: dateMainFontSize,
                                            lineHeight: '1.4',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {moment(new Date(appointment.startDate)).format('DD/MM/YYYY')}
                                    </Text>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                                        {t(moment(appointment.startDate).format('dddd'))}
                                    </Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#13c2c2', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                        <MdAccessTime />
                                        {moment(new Date(appointment.startDate)).format('HH:mm')} - {moment(new Date(appointment.endDate)).format('HH:mm')}
                                    </div>
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                                flexShrink: 0
                            }}>
                                {renderStateTag(appointment.state)}
                            </div>
                        </div>

                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12}>
                                <InfoRow
                                    iconColor={'#13c2c2'}
                                    icon={<MdPerson />}
                                    title={t('Doctor')}
                                    value={appointment.doctor?.name}
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoRow
                                    iconColor={'#13c2c2'}
                                    icon={<MdOutlineDescription />}
                                    title={t('Anamnesis') + ' / ' + t('Note')}
                                    value={
                                        (appointment.anamnez || appointment.note) ? (
                                            <div style={{
                                                maxHeight: isMobile ? 140 : 220,
                                                overflowY: 'auto',
                                                paddingRight: 4
                                            }}>
                                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                    {appointment.anamnez && (
                                                        <Text type="secondary" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            Anamnez: {appointment.anamnez}
                                                        </Text>
                                                    )}
                                                    {appointment.note && (
                                                        <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            Not: {appointment.note}
                                                        </Text>
                                                    )}
                                                </Space>
                                            </div>
                                        ) : '--'
                                    }
                                />
                            </Col>
                        </Row>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Check-in Alanı */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <Text type="secondary">{t('Check-in Status')}:</Text>

                            {!appointment.checkIn ? (
                                <Tag color="error" icon={<MdCancel style={{ position: 'relative', top: 1 }} />} style={{ fontSize: stateTagFontSize }}>
                                    {t('Not Checked In')}
                                </Tag>
                            ) : (
                                <Tag color="success" icon={<BsFillCheckCircleFill style={{ position: 'relative', top: 1 }} />} style={{ fontSize: stateTagFontSize }}>
                                    {t('Check-in')}: {appointment.checkInDate && moment(new Date(appointment.checkInDate)).format('HH:mm')}
                                </Tag>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
